import { useCallback, useRef } from 'react';
import { t, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { get } from 'lodash';
import { DELETE, DISABLE, ENABLE, RESCAN } from '../../../../services/FormDataTableActionService';
import { useDataService } from '../../../../services/ApiDataService';
import useUiToasts, { createToastConfig, ERROR, SUCCESS } from '../../../../stores/ToastStore';
import useActioningContext from '../../../../utilities/ActioningProviderUtilities';
import { getErrMessage } from '../../../../utilities/PromiseUtilities';
import {
    DETAIL_NO_NEED_ALERT,
    MONITORED_DETAIL_PRE_DELETE_URL,
    MONITORED_DETAIL_RESCAN_URL,
} from '../InfrastructureConstants';
import ConfirmDataTableActionButton from '../../../form/ConfirmDataTableActionButton';
import DeleteConfirmDialog from '../DeleteConfirmDialog';
import DisableDeleteConfirmDialog from '../DisableDeleteConfirmDialog';
import { canDelete, canReclassify, isDeviceDisabled } from './DeviceHelper';
import { buildDetailActionPayload, buildRescanPayload } from '../InfrastructureActionHelper';

/**
 * Renders action buttons for the device detail toolbar.
 * Buttons: RESCAN, DELETE, DISABLE, ENABLE.
 *
 * RESCAN uses a custom async handler with a different payload structure.
 * DELETE uses a two-phase flow: preDelete API call -> confirm dialog -> delete or disable.
 * DISABLE / ENABLE use the standard handleConfirmedDataTableAction callback.
 *
 * Uses ref-based dialog control pattern (consistent with List page implementation).
 */
const DeviceDetailActionButtons = ({
    showDataTableActionButton,
    isActioning,
    formData,
    handleConfirmedDataTableAction,
    recordId,
}) => {
    const dataService = useDataService();
    const { setIsActioning } = useActioningContext();
    const [, toastActions] = useUiToasts();

    // Refs for imperative dialog control
    const deleteConfirmDialogRef = useRef(null);
    const disableDeleteConfirmDialogRef = useRef(null);

    // RESCAN: custom handler with a different payload structure
    const handleRescan = useCallback(async () => {
        setIsActioning(true);
        try {
            const payload = buildRescanPayload(recordId);
            await dataService.post(MONITORED_DETAIL_RESCAN_URL, payload);
            toastActions.createToast(
                createToastConfig(SUCCESS, i18n._(t`Device will be rescanned within a few minutes`))
            );
        } catch (error) {
            toastActions.createToast(createToastConfig(ERROR, getErrMessage(error)));
        } finally {
            setIsActioning(false);
        }
    }, [recordId, dataService, setIsActioning, toastActions]);

    // DELETE: two-phase flow (preDelete -> dialog -> actual delete or disable)
    const handlePreDelete = useCallback(async () => {
        setIsActioning(true);
        try {
            const payload = buildDetailActionPayload(recordId);
            const response = await dataService.post(MONITORED_DETAIL_PRE_DELETE_URL, payload);
            const discovered = get(response, 'discovered', false);

            // Show appropriate dialog based on discovery status
            if (discovered) {
                // Discovered device: show 3-button dialog (Cancel/Delete/Disable)
                // For Detail page, always idCount=1 and discoveredCount=1
                disableDeleteConfirmDialogRef.current?.open(1, 1);
            } else {
                // Non-discovered device: show 2-button dialog (Cancel/Delete)
                deleteConfirmDialogRef.current?.open(1);
            }
        } catch (error) {
            toastActions.createToast(createToastConfig(ERROR, getErrMessage(error)));
        } finally {
            setIsActioning(false);
        }
    }, [recordId, dataService, setIsActioning, toastActions]);

    // Action handlers for delete/disable from dialogs
    const handleDeleteDataTableAction = useCallback(() => {
        handleConfirmedDataTableAction(null, DELETE, DETAIL_NO_NEED_ALERT);
    }, [handleConfirmedDataTableAction]);

    const handleDisableDataTableAction = useCallback(() => {
        handleConfirmedDataTableAction(null, DISABLE, DETAIL_NO_NEED_ALERT);
    }, [handleConfirmedDataTableAction]);

    if (!showDataTableActionButton) {
        return null;
    }

    const disabled = isDeviceDisabled(formData);
    const rescanEnabled = canReclassify(formData);
    const deleteEnabled = canDelete(formData);

    return (
        <>
            <ConfirmDataTableActionButton
                key={RESCAN}
                label={<Trans>RESCAN</Trans>}
                disabled={isActioning || !rescanEnabled}
                handleConfirmedDataTableAction={handleRescan}
                needConfirm={false}
            />
            <ConfirmDataTableActionButton
                key={DELETE}
                label={<Trans>DELETE</Trans>}
                disabled={isActioning || !deleteEnabled}
                handleConfirmedDataTableAction={handlePreDelete}
                needConfirm={false}
            />
            <ConfirmDataTableActionButton
                key={DISABLE}
                label={<Trans>DISABLE</Trans>}
                disabled={isActioning || disabled}
                handleConfirmedDataTableAction={(event) =>
                    handleConfirmedDataTableAction(event, DISABLE, null, DETAIL_NO_NEED_ALERT)
                }
                needConfirm={false}
            />
            <ConfirmDataTableActionButton
                key={ENABLE}
                label={<Trans>ENABLE</Trans>}
                disabled={isActioning || !disabled}
                handleConfirmedDataTableAction={(event) =>
                    handleConfirmedDataTableAction(event, ENABLE, null, DETAIL_NO_NEED_ALERT)
                }
                needConfirm={false}
            />
            <DeleteConfirmDialog
                handleDeleteDataTableAction={handleDeleteDataTableAction}
                ref={deleteConfirmDialogRef}
            />
            <DisableDeleteConfirmDialog
                handleDeleteDataTableAction={handleDeleteDataTableAction}
                handleDisableDataTableAction={handleDisableDataTableAction}
                ref={disableDeleteConfirmDialogRef}
            />
        </>
    );
};

export default DeviceDetailActionButtons;
